import React, { useEffect, useId, useRef, type RefObject } from 'react';
import { AlertTriangle } from 'lucide-react';
import useDialogFocus from './useDialogFocus';

interface ConfirmModalProps {
  open: boolean;
  title: string;
  /** Corpo del messaggio: una o più frasi, già in italiano corrente. */
  message: string;
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  /** Dove torna il fuoco alla chiusura: di norma il bottone che l'ha aperta. */
  returnRef?: RefObject<HTMLElement | null>;
  busy?: boolean;
}

/**
 * Conferma delle pagine del richiedente. Non riusa `components/ConfirmDialog`
 * perché quello non è una finestra di dialogo per chi usa la tastiera o un
 * lettore di schermo: niente `role="dialog"`, niente fuoco, niente Escape.
 * Qui il fuoco entra sul bottone primario, il Tab resta dentro, Escape annulla
 * e alla chiusura il fuoco torna da dove era partito.
 */
const ConfirmModal: React.FC<ConfirmModalProps> = ({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel = 'Annulla',
  onConfirm,
  onCancel,
  returnRef,
  busy = false,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();

  useDialogFocus(open, panelRef, { initialRef: confirmRef, returnRef });

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 dark:bg-black/60">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-xl p-5"
      >
        <div className="flex items-start gap-3">
          <span className="w-8 h-8 shrink-0 rounded-lg bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h3 id={titleId} className="text-sm font-semibold text-gray-900 dark:text-white">
              {title}
            </h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{message}</p>
          </div>
        </div>
        <div className="mt-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-sm font-medium text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy && (
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            )}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
